/**
 * ArtistInfoWidget Component
 *
 * Displays artist biography and metadata for concert events.
 * Fetches data from the artist-info-service microservice which uses
 * MusicBrainz + Wikipedia (no API keys required).
 *
 * Features:
 * - Artist photo from Wikipedia
 * - Short biography
 * - Genres, origin, years active
 * - Top albums
 * - Links to Wikipedia, Spotify, official site
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  User,
  Music,
  MapPin,
  Calendar,
  Disc,
  ExternalLink,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface ArtistInfo {
  name: string;
  bio: string | null;
  bioShort: string | null;
  description: string | null;
  image: string | null;
  imageLarge: string | null;
  type: string;
  origin: string | null;
  yearsActive: string | null;
  genres: string[];
  topAlbums: Array<{ title: string; year: string | null }>;
  links: {
    wikipedia: string | null;
    spotify: string | null;
    official: string | null;
  };
  mbid: string | null;
  source: {
    musicbrainz: boolean;
    wikipedia: boolean;
  };
}

interface ArtistInfoWidgetProps {
  /** Artist/performer name to look up */
  artistName: string;
  /** Optional: Collapse by default on mobile */
  defaultCollapsed?: boolean;
}

// Cache artist info in memory to avoid repeated API calls
const artistCache = new Map<string, { data: ArtistInfo; timestamp: number }>();
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Fetch artist info from microservice
 */
async function fetchArtistInfo(artistName: string): Promise<ArtistInfo | null> {
  // Check cache first
  const cacheKey = artistName.toLowerCase().trim();
  const cached = artistCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const serviceUrl =
      import.meta.env.VITE_ARTIST_INFO_SERVICE_URL ||
      'https://artist-info-service-production.up.railway.app';

    const response = await fetch(`${serviceUrl}/artist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artistName }),
    });

    if (!response.ok) {
      console.warn(`[ArtistInfoWidget] Service returned ${response.status}`);
      return null;
    }

    const data = await response.json();
    if (data.success && data.artist) {
      // Cache the result
      artistCache.set(cacheKey, { data: data.artist, timestamp: Date.now() });
      return data.artist;
    }

    return null;
  } catch (error) {
    console.error('[ArtistInfoWidget] Fetch error:', error);
    return null;
  }
}

/**
 * Genre Badge Component
 */
const GenreBadge: React.FC<{ genre: string }> = ({ genre }) => (
  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 capitalize">
    {genre}
  </span>
);

/**
 * Album Item Component
 */
const AlbumItem: React.FC<{ title: string; year: string | null }> = ({ title, year }) => (
  <div className="flex items-center gap-2 py-1.5">
    <Disc size={14} className="text-dark-muted flex-shrink-0" />
    <span className="text-sm text-dark-text truncate">{title}</span>
    {year && <span className="text-xs text-dark-muted">({year})</span>}
  </div>
);

/**
 * ArtistInfoWidget Component
 */
export const ArtistInfoWidget: React.FC<ArtistInfoWidgetProps> = ({
  artistName,
  defaultCollapsed = false,
}) => {
  const [artistInfo, setArtistInfo] = useState<ArtistInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [showFullBio, setShowFullBio] = useState(false);

  // Fetch artist info on mount or when artistName changes
  useEffect(() => {
    let cancelled = false;

    const loadArtistInfo = async () => {
      if (!artistName || artistName.trim().length < 2) {
        setLoading(false);
        setError('No artist name provided');
        return;
      }

      setLoading(true);
      setError(null);

      const info = await fetchArtistInfo(artistName);

      if (cancelled) return;

      if (info) {
        setArtistInfo(info);
        setError(null);
      } else {
        setError('Artist info not available');
      }
      setLoading(false);
    };

    loadArtistInfo();

    return () => {
      cancelled = true;
    };
  }, [artistName]);

  // Don't render if loading failed and no cached data
  if (error && !artistInfo) {
    return null; // Silently hide widget if no data
  }

  // Loading state
  if (loading && !artistInfo) {
    return (
      <div className="rounded-xl bg-dark-surface border border-dark-border/30 p-4">
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-dark-surface/50 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-dark-surface/50 rounded animate-pulse" />
            <div className="h-3 w-48 bg-dark-surface/50 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!artistInfo) return null;

  const bioToShow = showFullBio ? artistInfo.bio : artistInfo.bioShort;
  const hasFullBio = artistInfo.bio && artistInfo.bioShort && artistInfo.bio !== artistInfo.bioShort;

  return (
    <div className="rounded-xl bg-dark-surface border border-dark-border/30 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-dark-surface/40/50 transition-all duration-150"
      >
        <div className="flex items-center gap-3">
          {/* Artist Image */}
          {artistInfo.image ? (
            <img
              src={artistInfo.image}
              alt={artistInfo.name}
              className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30"
              onError={(e) => {
                // Hide image if it fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center border-2 border-purple-500/30">
              <User size={24} className="text-purple-400" />
            </div>
          )}

          <div className="text-left">
            <h3 className="text-sm font-bold text-white">About {artistInfo.name}</h3>
            <p className="text-xs text-dark-muted">
              {artistInfo.description || artistInfo.type}
            </p>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <div className="text-dark-muted">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Expandable Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-dark-border/30">
          {/* Biography */}
          {bioToShow && (
            <div className="pt-4">
              <p className="text-sm text-dark-text leading-relaxed">
                {bioToShow}
                {hasFullBio && (
                  <button
                    onClick={() => setShowFullBio(!showFullBio)}
                    className="ml-1 text-purple-400 hover:text-purple-300 font-medium"
                  >
                    {showFullBio ? 'Show less' : 'Read more'}
                  </button>
                )}
              </p>
            </div>
          )}

          {/* Quick Facts Row */}
          <div className="flex flex-wrap gap-3 text-sm">
            {artistInfo.origin && (
              <div className="flex items-center gap-1.5 text-dark-muted">
                <MapPin size={14} />
                <span>{artistInfo.origin}</span>
              </div>
            )}
            {artistInfo.yearsActive && (
              <div className="flex items-center gap-1.5 text-dark-muted">
                <Calendar size={14} />
                <span>{artistInfo.yearsActive}</span>
              </div>
            )}
          </div>

          {/* Genres */}
          {artistInfo.genres.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-dark-muted uppercase tracking-wide">
                Genres
              </h4>
              <div className="flex flex-wrap gap-2">
                {artistInfo.genres.slice(0, 5).map((genre, idx) => (
                  <GenreBadge key={idx} genre={genre} />
                ))}
              </div>
            </div>
          )}

          {/* Top Albums */}
          {artistInfo.topAlbums.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-dark-muted uppercase tracking-wide">
                Discography
              </h4>
              <div className="space-y-0.5">
                {artistInfo.topAlbums.slice(0, 4).map((album, idx) => (
                  <AlbumItem key={idx} title={album.title} year={album.year} />
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          {(artistInfo.links.wikipedia || artistInfo.links.spotify || artistInfo.links.official) && (
            <div className="flex flex-wrap gap-3 pt-2 border-t border-dark-border/30">
              {artistInfo.links.spotify && (
                <a
                  href={artistInfo.links.spotify}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400 hover:text-green-300"
                >
                  <Music size={14} />
                  Spotify
                  <ExternalLink size={12} />
                </a>
              )}
              {artistInfo.links.wikipedia && (
                <a
                  href={artistInfo.links.wikipedia}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-400 hover:text-blue-300"
                >
                  <ExternalLink size={14} />
                  Wikipedia
                </a>
              )}
              {artistInfo.links.official && (
                <a
                  href={artistInfo.links.official}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-400 hover:text-purple-300"
                >
                  <ExternalLink size={14} />
                  Official Site
                </a>
              )}
            </div>
          )}

          {/* Data Source Attribution */}
          <div className="text-xs text-dark-muted/70 pt-2">
            Data from{' '}
            {artistInfo.source.musicbrainz && 'MusicBrainz'}
            {artistInfo.source.musicbrainz && artistInfo.source.wikipedia && ' & '}
            {artistInfo.source.wikipedia && 'Wikipedia'}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistInfoWidget;
